/**
 * Display formatting.
 *
 * Everything is fixed to `en-PH` / `Asia/Manila` rather than the browser
 * locale. HR data is read against Philippine payroll and government deadlines;
 * a payslip date that renders differently because someone's laptop is set to
 * US locale is a support ticket waiting to happen.
 */
const LOCALE = 'en-PH';
const TIME_ZONE = 'Asia/Manila';

const dateFormatter = new Intl.DateTimeFormat(LOCALE, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: TIME_ZONE,
});

const dateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: TIME_ZONE,
});

const currencyFormatter = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2,
});

/**
 * Formats a `YYYY-MM-DD` calendar date.
 *
 * The `T00:00:00` suffix is load-bearing: `new Date('2026-03-01')` is parsed as
 * UTC midnight and renders as 28 February in Manila (UTC+8 west of the line).
 * Appending a local time forces local-calendar interpretation.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateTimeFormatter.format(date);
}

/** Money arrives from the API as a decimal string, never a JSON number. */
export function formatCurrency(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = typeof value === 'string' ? Number(value) : value;
  return Number.isNaN(amount) ? '—' : currencyFormatter.format(amount);
}

/** Whole years since a date — tenure and age. */
export function yearsSince(value: string | null | undefined): number | null {
  if (!value) return null;
  const start = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime())) return null;

  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  const monthDelta = now.getMonth() - start.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < start.getDate())) years -= 1;

  return years;
}

/** `2 years, 3 months`, for tenure display. */
export function formatTenure(dateHired: string | null | undefined): string {
  if (!dateHired) return '—';
  const start = new Date(`${dateHired.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime())) return '—';

  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) return '—';

  const years = Math.floor(months / 12);
  const remainder = months % 12;

  const parts: string[] = [];
  if (years > 0) parts.push(`${years} year${years === 1 ? '' : 's'}`);
  if (remainder > 0) parts.push(`${remainder} month${remainder === 1 ? '' : 's'}`);

  return parts.length > 0 ? parts.join(', ') : 'Less than a month';
}

/** Title-cases an enum value: `PROJECT_BASED` → `Project based`. */
export function humaniseEnum(value: string | null | undefined): string {
  if (!value) return '—';
  const lower = value.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
